import { Dispatch, SetStateAction, useCallback } from "react";

export declare type OnChangeKeyValue<TObj> = <TKey extends keyof TObj>(key: TKey, value: TObj[TKey]) => void;
export declare type OnChange<TObj> = (value: TObj) => void;
export declare type OnChangeDispatch<TObj> = Dispatch<SetStateAction<TObj>>;
export declare type SetStateActionFunction<TObj> = (prevState: TObj) => TObj;

function isSetter<T>(dispatch: SetStateAction<T>): dispatch is SetStateActionFunction<T> {
    return typeof (dispatch) === "function";
}

// NOTE: What is this?  Why is it called Dispatch?
export function useOnChangeChildDispatch<T, TKey extends keyof T>(key: TKey, setParent: OnChangeDispatch<T>): OnChangeDispatch<T[TKey]> {
    return useCallback(newChildState => {
        setParent(oldParentState => {
            const newChildValue: T[TKey] = isSetter<T[TKey]>(newChildState) ?
                newChildState(oldParentState && oldParentState[key]) :
                newChildState;
            return ({
                ...oldParentState,
                [key]: newChildValue
            });
        });
    }, [key, setParent]);
}

export function useOnChange<T>(obj: T, handler?: OnChange<T>): OnChangeKeyValue<T> {
    return useCallback<OnChangeKeyValue<T>>((key, value) => {
        handler({
            ...obj,
            [key]: value
        });
    }, [obj, handler]);
}

/**
 * Utility function to provide a type safe useCallback to update key values.
 *
 * Use within React Functional Components
 *
 * @param key - Key of the object for the handler function
 * @param handler - A function for a specific type, typically the components props, that updates the mutable value
 * @param modifier - Optional function to modify the value
 * @returns
 */
export function useOnChangeKeyValue<T, TKey extends keyof T>(key: TKey, handler: OnChangeKeyValue<T>, modifier?: (curr: T[TKey]) => T[TKey]) {
    return useCallback<OnChange<T[TKey]>>((value) => {
        if (typeof modifier === "function") {
            value = modifier(value);
        }
        handler(key, value);
    }, [modifier, handler, key]);
}

export function useAddArrayItem<T>(items: readonly T[], handler: (newValues: T[]) => void) {
    return useCallback((newItem: T) => {
        handler([...items, newItem]);
    }, [items, handler]);
}

type Parameters<T> = T extends (...args: infer TParams) => any ? TParams : never;

export function join<T extends (...args: any[]) => void>(left: T, right: T): T {
    return ((...params: Parameters<T>) => {
        left(...params);
        right(...params);
    }) as any as T;
}

export function useMultiCallback<T extends (...args: any[]) => void>(left: T, right: T): T {
    return useCallback<T>(join<T>(left, right), [left, right]);
}
